import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  apiAdminGetMasters,
  apiAdminCreateMaster,
  apiAdminUpdateMasterCap,
  apiAdminSetMasterStatus,
  apiAdminSetMasterStaging,
  apiAdminStagingFootprint,
  ApiRequestError,
  type AdminMaster,
  type StagingFootprint,
} from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Plus,
  RefreshCw,
  AlertCircle,
  Eye,
  EyeOff,
  X,
  Save,
  Loader2,
  ShieldOff,
  ShieldCheck,
  CheckCircle2,
  Users,
  FlaskConical,
} from 'lucide-react'
import { useT } from '@/contexts/language'
import { useCan } from '@/hooks/usePermission'

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive') return 'warning'
  return 'outline'
}

const pct = (rate: string) => Math.round(parseFloat(rate) * 100)

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="font-mono text-xs text-destructive flex items-center gap-1" role="alert">
      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
      {message}
    </p>
  )
}

// ─── Create master ────────────────────────────────────────────────────────────
const schema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(50)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, numbers, _ and -'),
  name: z.string().min(1, 'Name is required').max(100),
  username: z
    .string()
    .min(3, 'At least 3 characters')
    .max(50)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, numbers, _ and -'),
  password: z.string().min(8, 'At least 8 characters'),
  cap: z.string().regex(/^\d{1,3}$/, 'Pick a ceiling'),
})
type FormValues = z.infer<typeof schema>

function CreateMasterForm({
  onCreated,
  onCancel,
}: {
  onCreated: (code: string, username: string) => void
  onCancel: () => void
}) {
  const t = useT()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { cap: '80' },
  })

  async function onSubmit(values: FormValues) {
    setSubmitError(null)
    try {
      const res = await apiAdminCreateMaster({
        code: values.code,
        name: values.name,
        cap: (parseInt(values.cap, 10) / 100).toFixed(4),
        username: values.username,
        password: values.password,
      })
      onCreated(res.code, values.username)
    } catch (err) {
      setSubmitError(err instanceof ApiRequestError ? err.message : 'สร้างมาสเตอร์ไม่สำเร็จ')
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-[var(--radius)] border border-border bg-card overflow-hidden mb-4"
      noValidate
    >
      <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          สร้างมาสเตอร์ใหม่
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          aria-label="ยกเลิก"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="code" className="font-mono text-xs">
              โค้ด
            </Label>
            <Input id="code" placeholder="master-a" className="font-mono text-sm" {...register('code')} />
            <FieldError message={errors.code?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="name" className="font-mono text-xs">
              ชื่อ
            </Label>
            <Input id="name" placeholder="Master A" className="font-mono text-sm" {...register('name')} />
            <FieldError message={errors.name?.message} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="username" className="font-mono text-xs">
              {t.common.username}
            </Label>
            <Input
              id="username"
              placeholder="mastera"
              autoComplete="off"
              className="font-mono text-sm"
              {...register('username')}
            />
            <FieldError message={errors.username?.message} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password" className="font-mono text-xs">
              {t.common.password}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="font-mono text-sm pr-9"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                aria-label={showPassword ? 'ซ่อน' : 'แสดง'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <FieldError message={errors.password?.message} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5 max-w-[12rem]">
          <Label htmlFor="cap" className="font-mono text-xs">
            เพดานหุ้น (%)
          </Label>
          <select
            id="cap"
            className={cn(
              'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 font-mono text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            {...register('cap')}
          >
            {Array.from({ length: 101 }, (_, i) => (
              <option key={i} value={String(i)}>
                {i}%
              </option>
            ))}
          </select>
          <p className="font-mono text-[10px] text-muted-foreground">
            มาสเตอร์แบ่งเพดานนี้ให้เอเจนต์ ที่เหลือเป็นของบริษัท
          </p>
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2">
        {submitError && (
          <div className="flex items-start gap-2 p-3 border border-destructive/40 rounded-[var(--radius-sm)] bg-destructive/10" role="alert">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="font-mono text-xs text-destructive">{submitError}</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1 font-mono text-xs" onClick={onCancel}>
            ยกเลิก
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 font-mono text-xs font-semibold uppercase tracking-wider"
          >
            {isSubmitting ? 'กำลังสร้าง…' : 'สร้างมาสเตอร์'}
          </Button>
        </div>
      </div>
    </form>
  )
}

/**
 * Turning a line into the testing line lets the integration tester fire real wallet
 * calls at every agent under it, so the dangerous direction is ON. Show what the line
 * currently holds before allowing it — a master with live members and real turnover is
 * almost certainly the wrong one to flag.
 */
function StagingConfirmDialog({
  master, onConfirm, onClose,
}: {
  master: AdminMaster
  onConfirm: () => void
  onClose: () => void
}) {
  const [footprint, setFootprint] = useState<StagingFootprint | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiAdminStagingFootprint(master.id)
      .then(setFootprint)
      .catch((err: unknown) => setError(err instanceof ApiRequestError ? err.message : 'โหลดข้อมูลสายงานไม่สำเร็จ'))
  }, [master.id])

  const hasRealTraffic = footprint !== null
    && (footprint.memberCount > 0 || parseFloat(footprint.recentStake) > 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="staging-confirm-title"
        className="w-full max-w-md rounded-[var(--radius)] border border-border bg-card p-5 shadow-lg">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-warning" />
          <h2 id="staging-confirm-title" className="text-base font-semibold">
            ตั้ง {master.code} เป็นสายทดสอบ?
          </h2>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          เอเจนต์ทุกคนในสายนี้จะถูกยิงคำสั่งทดสอบได้ — รวมถึงคำสั่ง <strong>หักเงิน</strong> และ
          <strong> จ่ายเงิน</strong> ซึ่งขยับยอดจริงในระบบของเอเจนต์
        </p>

        {error ? (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        ) : footprint === null ? (
          <Skeleton className="mt-3 h-16 w-full" />
        ) : (
          <dl className="mt-3 grid grid-cols-3 gap-2 rounded-[var(--radius-sm)] bg-muted/60 p-3 text-center">
            <div>
              <dt className="text-[11px] text-muted-foreground">เอเจนต์</dt>
              <dd className="font-mono text-sm font-semibold">{footprint.agentCount}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-muted-foreground">สมาชิก</dt>
              <dd className="font-mono text-sm font-semibold">{footprint.memberCount}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-muted-foreground">ยอดแทง 7 วัน</dt>
              <dd className="font-mono text-sm font-semibold">
                {Number(footprint.recentStake).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </dd>
            </div>
          </dl>
        )}

        {hasRealTraffic && (
          <p className="mt-3 rounded-[var(--radius-sm)] bg-destructive/5 p-2.5 text-xs leading-relaxed text-destructive">
            สายนี้มีสมาชิกและยอดแทงจริงอยู่ — ตรวจให้แน่ใจว่านี่คือสายทดสอบ ไม่ใช่สายที่ใช้งานจริง
          </p>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onClose}>ยกเลิก</Button>
          <Button onClick={onConfirm} disabled={footprint === null && !error}>ยืนยัน</Button>
        </div>
      </div>
    </div>
  )
}

// ─── One master row: inline cap edit (never below floor) + suspend ──────────────
function MasterRow({ master, onChanged }: { master: AdminMaster; onChanged: () => void }) {
  const navigate = useNavigate()
  const can = useCan()
  const capPct = pct(master.commissionRate)
  const floorPct = pct(master.maxAgentShare)
  const [cap, setCap] = useState(String(capPct))
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)
  const [confirmStaging, setConfirmStaging] = useState(false)
  const dirty = parseInt(cap, 10) !== capPct

  async function setStaging(next: boolean) {
    setRowError(null)
    setConfirmStaging(false)
    try {
      await apiAdminSetMasterStaging(master.id, next)
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiRequestError ? err.message : 'เปลี่ยนสายทดสอบไม่สำเร็จ')
    }
  }

  async function saveCap() {
    setSaving(true)
    setRowError(null)
    try {
      await apiAdminUpdateMasterCap(master.id, (parseInt(cap, 10) / 100).toFixed(4))
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiRequestError ? err.message : 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  async function toggleStatus() {
    setToggling(true)
    setRowError(null)
    try {
      await apiAdminSetMasterStatus(master.id, master.status === 'active' ? 'suspended' : 'active')
      onChanged()
    } catch (err) {
      setRowError(err instanceof ApiRequestError ? err.message : 'เปลี่ยนสถานะไม่สำเร็จ')
    } finally {
      setToggling(false)
    }
  }

  return (
    <TableRow>
      <TableCell>
        <p className="font-mono text-sm font-semibold">{master.name}</p>
        <p className="font-mono text-xs text-muted-foreground">{master.code}</p>
        {rowError && <p className="font-mono text-[10px] text-destructive mt-1">{rowError}</p>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <select
            aria-label={`เพดานของ ${master.name}`}
            className={cn(
              'h-8 rounded-[var(--radius-sm)] border border-input bg-background px-2 font-mono text-xs',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            value={cap}
            onChange={(e) => setCap(e.target.value)}
          >
            {/* floor..100 — can't go below the highest agent share already granted */}
            {Array.from({ length: 101 - floorPct }, (_, i) => floorPct + i).map((v) => (
              <option key={v} value={String(v)}>
                {v}%
              </option>
            ))}
          </select>
          {dirty && can('masters:manage') && (
            <Button
              size="sm"
              className="h-8 gap-1 font-mono text-xs"
              onClick={() => void saveCap()}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              บันทึก
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-sm text-muted-foreground">
        {master.agentCount}
      </TableCell>
      <TableCell className="text-right font-mono text-xs text-muted-foreground">
        {floorPct > 0 ? `${floorPct}%` : '—'}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant(master.status)} className="text-[10px]">
          {master.status}
        </Badge>
      </TableCell>
      <TableCell>
        {/* Off → on asks first; on → off is always safe, so it just happens. */}
        {can('masters:staging') ? (
        <button
          type="button"
          role="switch"
          aria-checked={master.isStaging}
          aria-label={`สายทดสอบของ ${master.name}`}
          onClick={() => (master.isStaging ? void setStaging(false) : setConfirmStaging(true))}
          className={cn(
            'inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            master.isStaging ? 'bg-warning' : 'bg-muted-foreground/30',
          )}
        >
          <span className={cn(
            'h-4 w-4 rounded-full bg-background shadow transition-transform',
            master.isStaging ? 'translate-x-4' : 'translate-x-0.5',
          )} />
        </button>
        ) : (
          // Read-only: still say which line this is, just without the way to change it.
          <span className="font-mono text-[10px] text-muted-foreground">
            {master.isStaging ? 'สายทดสอบ' : '—'}
          </span>
        )}
        {confirmStaging && (
          <StagingConfirmDialog
            master={master}
            onConfirm={() => void setStaging(true)}
            onClose={() => setConfirmStaging(false)}
          />
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 font-mono text-xs"
            onClick={() => navigate(`/admin/masters/${master.id}/agents`)}
          >
            <Users className="h-3 w-3" />
            ดูสายงาน
          </Button>
          {can('masters:manage') && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 gap-1.5 font-mono text-xs',
              master.status === 'active'
                ? 'text-destructive hover:text-destructive hover:bg-destructive/10'
                : 'text-success hover:text-success hover:bg-success/10',
            )}
            onClick={() => void toggleStatus()}
            disabled={toggling}
          >
            {toggling ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : master.status === 'active' ? (
              <ShieldOff className="h-3 w-3" />
            ) : (
              <ShieldCheck className="h-3 w-3" />
            )}
            {master.status === 'active' ? 'ระงับ' : 'เปิด'}
          </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────────
export default function AdminMastersPage() {
  const t = useT()
  const can = useCan()
  const [masters, setMasters] = useState<AdminMaster[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'create'>('list')
  const [created, setCreated] = useState<{ code: string; username: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setMasters(await apiAdminGetMasters())
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [t.common.error])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">
            {t.nav.masters}
          </h1>
          <p className="font-mono text-xs text-muted-foreground mt-0.5">
            มาสเตอร์แบ่งเพดานหุ้นให้เอเจนต์ · ลดเพดานต่ำกว่าหุ้นเอเจนต์สูงสุดที่เปิดไว้ไม่ได้
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
            className="font-mono text-xs gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            {t.common.refresh}
          </Button>
          {view === 'list' && can('masters:manage') && (
            <Button size="sm" className="font-mono text-xs gap-1.5" onClick={() => setView('create')}>
              <Plus className="h-3.5 w-3.5" />
              สร้างมาสเตอร์
            </Button>
          )}
        </div>
      </div>

      {created && (
        <div className="rounded-[var(--radius)] border border-success/40 bg-success/5 p-4 mb-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
          <div className="font-mono text-xs">
            <p className="font-bold text-success">
              สร้างมาสเตอร์ <span className="font-semibold">{created.code}</span> แล้ว
            </p>
            <p className="text-muted-foreground mt-1">
              login: <span className="text-foreground font-semibold">{created.username}</span> — ให้มาสเตอร์เข้าที่ agent.sttv169.xyz
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreated(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
            aria-label="ปิด"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {view === 'create' && (
        <CreateMasterForm
          onCreated={(code, username) => {
            setCreated({ code, username })
            setView('list')
            void load()
          }}
          onCancel={() => setView('list')}
        />
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.nav.masters}
          </span>
          {!loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{masters.length} รายการ</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="ml-auto h-5 w-16" />
              </div>
            ))}
          </div>
        ) : masters.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">ยังไม่มีมาสเตอร์</p>
            {view === 'list' && (
              <Button size="sm" className="mt-4 font-mono text-xs gap-1.5" onClick={() => setView('create')}>
                <Plus className="h-3.5 w-3.5" />
                สร้างมาสเตอร์
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>มาสเตอร์</TableHead>
                <TableHead>เพดานหุ้น</TableHead>
                <TableHead className="text-right">เอเจนต์</TableHead>
                <TableHead className="text-right">หุ้นสูงสุดที่เปิด</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>สายทดสอบ</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {masters.map((m) => (
                <MasterRow key={m.id} master={m} onChanged={() => void load()} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
